import { MutationCache, QueryCache } from "@tanstack/react-query";
import { isErrorHandled, resolveErrorMessage } from "./errorMessage";
import { notifyError } from "./toastNotifier";

// T2 (2026-09-19, qa-bug-matrix-2026-09.md §6, corrección post-cierre del
// intento 1 de `t2-foundation`): fábricas separadas de `main.tsx` para que
// el mecanismo se pueda probar sin importar `main.tsx` (que monta la app de
// verdad al importarse). `createGlobalErrorFallback` en `test/portal/
// queryErrorFallback.test.tsx` ejercita esto contra un `QueryClient` real.
function reportUnhandledError(error: unknown) {
  const resolved = resolveErrorMessage(error);
  notifyError(resolved.message, { correlationId: resolved.correlationId });
}

/**
 * Las listas/detalle (`useQuery`) casi nunca traen su propio manejo de
 * error: v5 de TanStack Query eliminó `onError` de `useQuery` por completo
 * (no hay un segundo callback con el que este fallback pueda competir ni
 * superponerse). Por eso alcanza con la salida estática
 * `query.meta?.toastHandled` — sin diferir nada — para el único caso real:
 * un resultado de error que la propia pantalla ya interpreta como estado
 * normal (`SessionProvider`, 401 sin sesión todavía).
 */
export function createQueryCache(): QueryCache {
  return new QueryCache({
    onError: (error, query) => {
      if (query.meta?.["toastHandled"]) return;
      reportUnhandledError(error);
    },
  });
}

/**
 * Las mutaciones sí compiten: `MutationCache.onError` se invoca
 * incondicionalmente y ANTES que el `onError` del sitio de la llamada
 * (verificado contra el código fuente instalado de `@tanstack/query-core`
 * — `mutation.js`/`mutationObserver.js` son tres callbacks independientes,
 * ninguno se entera de los otros). Sin diferir, cualquier pantalla con
 * manejo propio completo mostraba DOS toasts, el genérico primero — el
 * peor orden posible.
 *
 * El `onError` de la mutación corre en el mismo tick, justo después del de
 * la caché, así que basta un macrotask (`setTimeout(fn, 0)`; un microtask
 * llega demasiado pronto, antes de que el `onError` de la pantalla se haya
 * ejecutado) para dejarlo terminar antes de decidir. Si para entonces el
 * error ya está en `isErrorHandled` (la pantalla llamó a
 * `markErrorHandled(error)` justo después de mostrar su propio mensaje —
 * ver `errorMessage.ts`), el fallback se calla; si nadie habló, habla él.
 * Ninguna pantalla tiene que declarar nada aparte: `t2-screens` solo llama
 * a `markErrorHandled` donde ya resuelve su mensaje.
 *
 * `mutation.meta?.toastHandled` queda como salida estática adicional para
 * una mutación que no pasa por ningún `onError` en absoluto pero cuya UI ya
 * se hace cargo del error por otro medio.
 */
export function createMutationCache(): MutationCache {
  return new MutationCache({
    onError: (error, _variables, _context, mutation) => {
      if (mutation.meta?.["toastHandled"]) return;
      setTimeout(() => {
        if (isErrorHandled(error)) return;
        reportUnhandledError(error);
      }, 0);
    },
  });
}
